import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { B } from './constants';
import { borderRadius, transitions } from './designTokens';
import { GBWordmark } from './components/Logo';
import { useAuth } from './AuthContext';
import { useCurrency } from './CurrencyContext';
import { SUPPORTED_LANGUAGES } from './i18n';
import UserMenu from './UserMenu';
import './Navbar.css';

// Compact language + currency dropdowns rendered in the navbar. Kept
// dependency-free (native selects) so they work on mobile and in tests.
function LocalePicker() {
  const { i18n, t } = useTranslation();
  const { currency, setCurrency, supportedCurrencies } = useCurrency();
  const selectStyle = {
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: borderRadius.pill,
    padding: '6px 8px',
    fontSize: 12,
    fontFamily: "'Montserrat',sans-serif",
    cursor: 'pointer',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} className="navbar-locale-picker">
      <select
        aria-label={t('nav.language')}
        value={i18n.language?.split('-')[0] || 'en'}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
        style={selectStyle}
      >
        {SUPPORTED_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code} style={{ color: '#000' }}>{l.flag} {l.code.toUpperCase()}</option>
        ))}
      </select>
      <select
        aria-label={t('nav.currency')}
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
        style={selectStyle}
      >
        {supportedCurrencies.map((c) => (
          <option key={c} value={c} style={{ color: '#000' }}>{c}</option>
        ))}
      </select>
    </div>
  );
}

const TabIcon = ({ name }) => {
  const icons = {
    home: '<path d="M3 11l9-8 9 8M5 10v10h14V10"/>',
    plan: '<rect x="4" y="7" width="16" height="13" rx="2"/><path d="M9 7V4h6v3M9 13h6"/>',
    reels: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="m10 9 5 3-5 3z"/>',
    profile: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.5-6 8-6s8 2 8 6"/>',
  };
  const d = icons[name] || icons.home;
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: d }} />;
};

export default function Navbar({ screen, goScreen, onAuthRequired }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  const links = [
    { id: 'home',    label: t('nav.home') },
    { id: 'planner', label: t('nav.aiPlanner') },
    { id: 'reels',   label: t('nav.reels') },
    { id: 'trips',   label: t('nav.myTrips') },
    { id: 'blogs',   label: t('nav.blogs') },
  ];

  const bottomTabs = [
    { id: 'home',    icon: 'home',    label: t('nav.home') },
    { id: 'planner', icon: 'plan',    label: t('nav.aiPlan') },
    { id: 'reels',   icon: 'reels',   label: t('nav.reels') },
    { id: 'profile', icon: 'profile', label: user ? t('nav.profile') : t('nav.signIn') },
  ];

  return (
    <>
      <nav style={{
        background: 'rgba(15,25,35,0.94)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        padding: '0 clamp(12px, 3vw, 40px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 64, position: 'sticky', top: 0, zIndex: 200,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <button onClick={() => goScreen('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <GBWordmark size="sm" />
        </button>

        <div className="navbar-links">
          {links.map(({ id, label }) => (
            <button key={id} className="focus-ring" onClick={() => goScreen(id)}
              style={{
                background: screen === id ? 'rgba(78,203,141,0.16)' : 'none',
                border: 'none', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                fontSize: 13, fontWeight: screen === id ? 700 : 600,
                color: screen === id ? B.mint : '#cdd5dc',
                padding: '8px 14px', borderRadius: borderRadius.pill,
                transition: `all ${transitions.fast}`,
              }}
              onMouseEnter={e => { if (screen !== id) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={e => { if (screen !== id) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#cdd5dc'; } }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <LocalePicker />
          <button className="navbar-plan-trip" onClick={() => goScreen('planner')}
            style={{
              background: B.amber, color: B.night, border: 'none',
              padding: '9px 20px', borderRadius: borderRadius.pill,
              cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
              fontSize: 12, fontWeight: 700,
              transition: `all ${transitions.fast}`,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z"/></svg>
            {t('nav.planTrip')}
          </button>
          <button className="navbar-hamburger focus-ring" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu"
            style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer', padding: '8px' }}>
            {menuOpen ? '✕' : '☰'}
          </button>
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserMenu goScreen={goScreen} />
            </div>
          ) : (
            <button className="navbar-signin" onClick={() => onAuthRequired()}
              style={{
                background: 'none', border: '1.5px solid rgba(255,255,255,0.25)', color: '#fff',
                padding: '8px 18px', borderRadius: borderRadius.pill,
                cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                fontSize: 12, fontWeight: 600, transition: `all ${transitions.fast}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
              {t('nav.signIn')}
            </button>
          )}
        </div>
      </nav>

      {menuOpen && (
        <div className="navbar-mobile-menu open">
          {links.map(({ id, label }) => (
            <button key={id} onClick={() => { goScreen(id); setMenuOpen(false); }}
              style={{
                background: screen === id ? 'rgba(78,203,141,0.16)' : 'none',
                border: 'none', color: screen === id ? B.mint : '#cdd5dc',
                cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                fontSize: 14, fontWeight: 600,
                padding: '12px 16px', textAlign: 'left', width: '100%',
                borderRadius: borderRadius.sm, transition: `all ${transitions.fast}`,
              }}>
              {label}
            </button>
          ))}
          {!user && (
            <button onClick={() => { goScreen('auth'); setMenuOpen(false); }}
              style={{
                background: 'rgba(245,166,35,0.15)', border: `1px solid ${B.amber}40`, color: B.amber,
                cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                fontSize: 14, fontWeight: 700,
                padding: '12px 16px', textAlign: 'center', width: '100%',
                borderRadius: borderRadius.sm, marginTop: 8,
              }}>
              {t('nav.signIn')}
            </button>
          )}
          <div className="navbar-mobile-locale">
            <LocalePicker />
          </div>
        </div>
      )}

      <div className="navbar-bottom-tabs">
        {bottomTabs.map(({ id, icon, label }) => {
          const isProfile = id === 'profile';
          const isActive = isProfile
            ? screen === 'trips' || screen === 'settings' || screen === 'profile'
            : screen === id;
          const handleClick = () => isProfile
            ? (user ? goScreen('settings') : goScreen('auth'))
            : goScreen(id);
          return (
            <button key={id} onClick={handleClick} aria-label={label} className="focus-ring"
              style={{
                background: 'none', border: 'none',
                color: isActive ? B.mint : '#8fa0ad',
                cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 3,
                fontSize: 10, fontFamily: "'Montserrat',sans-serif",
                fontWeight: isActive ? 700 : 600,
                padding: '4px 10px', borderRadius: 14,
                transition: `all ${transitions.fast}`,
              }}>
              <span style={{
                width: 30, height: 30, display: 'grid', placeItems: 'center',
                borderRadius: 10,
                background: isActive ? 'rgba(78,203,141,0.16)' : 'transparent',
                transition: `all ${transitions.fast}`,
              }}>
                <TabIcon name={icon} />
              </span>
              {label}
            </button>
          );
        })}
      </div>
    </>
  );
}
